import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLoadScript, GoogleMap, OverlayView, Polyline } from '@react-google-maps/api';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Car, MapPin, Clock, X, Radio, Users, ChevronRight, AlertCircle, UserPlus, Plane } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AdminLayout from '../components/AdminLayout';
import { formatUkDate } from '../lib/date';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAPS_LIBRARIES = ['places'];

const STATUS_META = {
  on_the_way: { label: 'On The Way', tone: 'bg-purple-100 text-purple-800' },
  arrived: { label: 'Arrived', tone: 'bg-blue-100 text-blue-800' },
  pob: { label: 'POB', tone: 'bg-emerald-100 text-emerald-800' },
};

const DEFAULT_CENTER = { lat: 53.4808, lng: -2.2426 }; // Manchester
const POLL_MS = 15000;

const timeAgo = (iso) => {
  if (!iso) return '—';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 0) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

// Pull vehicle registration plate out of a free-text "vehicle" string like "Mercedes V Class (SN25 OAE)"
const extractPlate = (vehicle) => {
  if (!vehicle) return '';
  const m = vehicle.match(/\(([^)]+)\)/);
  return (m ? m[1] : vehicle).toUpperCase().replace(/\s+/g, '');
};

// Custom HTML map marker (orange/blue pill with car icon, like the reference)
const VehiclePin = ({ color, label }) => (
  <div
    style={{ transform: 'translate(-50%, -100%)' }}
    className="pointer-events-auto select-none"
  >
    <div
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md shadow-lg text-white font-semibold text-xs whitespace-nowrap"
      style={{ backgroundColor: color }}
    >
      <Car className="h-3.5 w-3.5" />
      <span>{label}</span>
    </div>
    <div
      className="w-0 h-0 mx-auto"
      style={{
        borderLeft: '7px solid transparent',
        borderRight: '7px solid transparent',
        borderTop: `9px solid ${color}`,
      }}
    />
  </div>
);

const LiveTracking = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GMAPS_LIBRARIES,
  });

  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [assigningId, setAssigningId] = useState(null);   // booking id being acted on
  const [pendingDriverId, setPendingDriverId] = useState({}); // bookingId -> driverId selection
  const [savingAssign, setSavingAssign] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [driverDetail, setDriverDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [, setTick] = useState(0); // re-render relative timestamps
  const mapRef = useRef(null);

  const fetchActive = useCallback(async () => {
    try {
      const [activeRes, driversRes, unassignedRes] = await Promise.all([
        axios.get(`${API}/admin/tracking/active`),
        axios.get(`${API}/drivers`),
        axios.get(`${API}/admin/tracking/unassigned`),
      ]);
      setBookings(activeRes.data.bookings || []);
      setDrivers(driversRes.data.drivers || []);
      setUnassigned(unassignedRes.data.bookings || []);
    } catch (e) {
      toast.error('Failed to load tracking data');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQuickAssign = async (bookingId) => {
    const driverId = pendingDriverId[bookingId];
    if (!driverId) {
      toast.error('Pick a driver first');
      return;
    }
    setSavingAssign(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/assign-driver`, { driverId });
      toast.success('Driver assigned · notification sent');
      setAssigningId(null);
      setPendingDriverId((prev) => {
        const { [bookingId]: _, ...rest } = prev;
        return rest;
      });
      fetchActive();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign driver');
    } finally {
      setSavingAssign(false);
    }
  };

  useEffect(() => {
    fetchActive();
    const id = setInterval(fetchActive, POLL_MS);
    const tickId = setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      clearInterval(id);
      clearInterval(tickId);
    };
  }, [fetchActive]);

  // One pin per driver (their currently live booking)
  const driverPins = useMemo(() => {
    const byDriver = new Map();
    bookings.forEach((b) => {
      if (!b.assignedDriverId || !b.driverLat || !b.driverLng) return;
      // Prefer the most recent ping if multiple live jobs per driver
      const existing = byDriver.get(b.assignedDriverId);
      if (
        !existing ||
        (b.driverLocationUpdatedAt || '') > (existing.driverLocationUpdatedAt || '')
      ) {
        byDriver.set(b.assignedDriverId, b);
      }
    });
    return Array.from(byDriver.values());
  }, [bookings]);

  // Fit map to pins on first load
  useEffect(() => {
    if (!mapRef.current || !window.google || driverPins.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    driverPins.forEach((b) => bounds.extend({ lat: b.driverLat, lng: b.driverLng }));
    mapRef.current.fitBounds(bounds, 120);
  }, [driverPins.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open driver detail panel
  const openDriver = async (driverId, focus = true) => {
    setSelectedDriverId(driverId);
    setLoadingDetail(true);
    setDriverDetail(null);
    try {
      const { data } = await axios.get(`${API}/admin/drivers/${encodeURIComponent(driverId)}/jobs`);
      setDriverDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load driver');
    } finally {
      setLoadingDetail(false);
    }
    // Pan map to that driver's pin
    if (focus && mapRef.current) {
      const pin = driverPins.find((b) => b.assignedDriverId === driverId);
      if (pin) {
        mapRef.current.panTo({ lat: pin.driverLat, lng: pin.driverLng });
        mapRef.current.setZoom(Math.max(mapRef.current.getZoom(), 12));
      }
    }
  };

  const closeDriver = () => {
    setSelectedDriverId(null);
    setDriverDetail(null);
  };

  // Build the live-drivers list for the floating panel
  // Live = drivers with a current pin. Idle = drivers with no live job.
  const liveDriverIds = useMemo(
    () => new Set(driverPins.map((b) => b.assignedDriverId)),
    [driverPins]
  );

  const driversForPanel = useMemo(() => {
    return drivers
      .map((d) => {
        const livePin = driverPins.find((b) => b.assignedDriverId === d.id);
        const isLive = !!livePin;
        return {
          ...d,
          live: isLive,
          plate: extractPlate(d.vehicle),
          subline: isLive
            ? (STATUS_META[livePin.driverStatus]?.label || 'Live')
            : (d.vehicle ? extractPlate(d.vehicle) : 'Idle'),
        };
      })
      .sort((a, b) => Number(b.live) - Number(a.live));
  }, [drivers, driverPins]);

  const selectedLivePin = useMemo(
    () => driverPins.find((b) => b.assignedDriverId === selectedDriverId),
    [driverPins, selectedDriverId]
  );

  return (
    <AdminLayout
      title="Live Tracking"
      subtitle={`${driverPins.length} live · ${driversForPanel.filter((d) => !d.live).length} idle`}
    >
      {loadError ? (
        <p className="text-red-600">Failed to load Google Maps.</p>
      ) : !isLoaded || loading ? (
        <p className="text-sm text-gray-600">Loading…</p>
      ) : drivers.length === 0 ? (
        <Card className="p-10 text-center" data-testid="tracking-empty-state">
          <Car className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-700 font-medium">No drivers added yet.</p>
        </Card>
      ) : (
        <div className="relative w-full" style={{ height: 'calc(100vh - 150px)' }} data-testid="tracking-map-card">
          {/* MAP */}
          <GoogleMap
            onLoad={(map) => { mapRef.current = map; }}
            mapContainerStyle={{ width: '100%', height: '100%', borderRadius: 12, overflow: 'hidden' }}
            center={DEFAULT_CENTER}
            zoom={9}
            options={{
              disableDefaultUI: false,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
              zoomControl: true,
              styles: [
                { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
              ],
            }}
          >
            {/* Trails */}
            {driverPins.map((b) =>
              Array.isArray(b.driverTrail) && b.driverTrail.length > 1 ? (
                <Polyline
                  key={`trail-${b.id}`}
                  path={b.driverTrail.map((p) => ({ lat: p.lat, lng: p.lng }))}
                  options={{
                    strokeColor: b.driverColor || '#475569',
                    strokeOpacity: 0.85,
                    strokeWeight: 4,
                    geodesic: true,
                    icons: [
                      {
                        icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.6, scale: 3 },
                        offset: '0',
                        repeat: '12px',
                      },
                    ],
                  }}
                />
              ) : null
            )}

            {/* Custom HTML pins */}
            {driverPins.map((b) => (
              <OverlayView
                key={`pin-${b.id}`}
                position={{ lat: b.driverLat, lng: b.driverLng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={() => ({ x: 0, y: 0 })}
              >
                <button
                  type="button"
                  onClick={() => openDriver(b.assignedDriverId, false)}
                  className="bg-transparent border-0 p-0 cursor-pointer"
                  data-testid={`tracking-pin-${b.assignedDriverId}`}
                  aria-label={b.driverName}
                >
                  <VehiclePin
                    color={b.driverColor || '#475569'}
                    label={extractPlate(b.driverVehicle) || b.driverName}
                  />
                </button>
              </OverlayView>
            ))}
          </GoogleMap>

          {/* FLOATING DRIVERS PANEL */}
          <div
            className="absolute top-4 left-4 w-72 bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
            data-testid="tracking-drivers-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-700" />
                <p className="font-semibold text-black text-sm">Drivers</p>
              </div>
              <span className="text-xs font-bold text-gray-500 bg-gray-100 rounded-full h-6 w-6 flex items-center justify-center">
                {drivers.length}
              </span>
            </div>
            <div className="max-h-[60vh] overflow-y-auto py-1">
              {driversForPanel.map((d) => {
                const isSelected = d.id === selectedDriverId;
                return (
                  <button
                    type="button"
                    key={d.id}
                    onClick={() => openDriver(d.id)}
                    className={`w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-gray-50' : ''
                    }`}
                    data-testid={`driver-pill-${d.id}`}
                  >
                    <div
                      className="h-10 w-10 rounded-full text-white flex items-center justify-center text-sm font-bold shrink-0"
                      style={{ backgroundColor: d.color || '#475569' }}
                    >
                      {initials(d.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-black text-sm truncate">{d.name}</p>
                      <p className="text-xs text-gray-500 truncate">{d.subline}</p>
                    </div>
                    <span
                      className={`h-2 w-2 rounded-full ${d.live ? 'animate-pulse' : ''}`}
                      style={{ backgroundColor: d.live ? d.color || '#475569' : '#cbd5e1' }}
                      aria-label={d.live ? 'Live' : 'Idle'}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* UNASSIGNED BOOKINGS PILL + PANEL */}
          {unassigned.length > 0 && (
            <>
              {!showUnassigned && (
                <button
                  type="button"
                  onClick={() => setShowUnassigned(true)}
                  className="absolute top-4 left-[20rem] bg-white rounded-full shadow-xl border border-amber-200 px-4 py-2.5 flex items-center gap-2 hover:bg-amber-50 transition-all"
                  data-testid="unassigned-pill"
                >
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-semibold text-black">
                    {unassigned.length} unassigned
                  </span>
                </button>
              )}

              {showUnassigned && (
                <div
                  className="absolute top-4 right-4 w-[400px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
                  style={{ maxHeight: 'calc(100vh - 180px)' }}
                  data-testid="unassigned-panel"
                >
                  <div className="bg-amber-50 px-4 py-3 flex items-center justify-between border-b border-amber-200">
                    <div className="flex items-center gap-2">
                      <UserPlus className="h-4 w-4 text-amber-800" />
                      <p className="font-semibold text-amber-900 text-sm">Unassigned Bookings</p>
                    </div>
                    <button onClick={() => setShowUnassigned(false)} className="text-amber-800 hover:text-black" data-testid="unassigned-close">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 230px)' }}>
                    {unassigned.map((b) => {
                      const customerName = [b.firstName, b.lastName].filter(Boolean).join(' ');
                      const isOpen = assigningId === b.id;
                      return (
                        <div key={b.id} className="border-b border-gray-100 last:border-0" data-testid={`unassigned-row-${b.id}`}>
                          <div className="px-4 py-3">
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <div className="min-w-0">
                                <p className="font-bold text-black">{b.pickupTime}</p>
                                <p className="text-[11px] text-gray-500">{formatUkDate(b.pickupDate)}</p>
                              </div>
                              {b.flightNumber && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-mono text-neutral-600 uppercase bg-neutral-100 rounded px-1.5 py-0.5">
                                  <Plane className="h-3 w-3" /> {String(b.flightNumber).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="space-y-1 text-sm mb-2">
                              <div className="flex items-start gap-2">
                                <span className="h-2 w-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                                <span className="text-neutral-800 truncate">{b.pickupLocation}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="h-2 w-2 rounded-sm bg-red-600 mt-1.5 shrink-0" />
                                <span className="text-neutral-600 truncate">{b.dropoffLocation}</span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                              <span className="truncate">{customerName} · {b.vehicleName}</span>
                            </div>

                            {!isOpen ? (
                              <Button
                                onClick={() => setAssigningId(b.id)}
                                variant="outline"
                                className="w-full border-black text-black hover:bg-black hover:text-white"
                                data-testid={`quick-assign-open-${b.id}`}
                              >
                                <UserPlus className="h-4 w-4 mr-2" /> Quick Assign
                              </Button>
                            ) : (
                              <div className="space-y-2">
                                <select
                                  value={pendingDriverId[b.id] || ''}
                                  onChange={(e) => setPendingDriverId((p) => ({ ...p, [b.id]: e.target.value }))}
                                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                                  data-testid={`quick-assign-select-${b.id}`}
                                >
                                  <option value="">Choose a driver…</option>
                                  {drivers.map((d) => (
                                    <option key={d.id} value={d.id}>
                                      {d.name}{d.vehicleReg ? ` (${d.vehicleReg})` : ''}
                                    </option>
                                  ))}
                                </select>
                                <div className="grid grid-cols-2 gap-2">
                                  <Button
                                    onClick={() => handleQuickAssign(b.id)}
                                    disabled={savingAssign || !pendingDriverId[b.id]}
                                    className="bg-black text-white hover:bg-neutral-900"
                                    data-testid={`quick-assign-confirm-${b.id}`}
                                  >
                                    {savingAssign ? 'Assigning…' : 'Assign'}
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      setAssigningId(null);
                                      setPendingDriverId((p) => {
                                        const { [b.id]: _, ...rest } = p;
                                        return rest;
                                      });
                                    }}
                                    variant="outline"
                                    className="border-gray-300"
                                    data-testid={`quick-assign-cancel-${b.id}`}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {selectedDriverId && (
            <div
              className="absolute top-4 right-4 w-[400px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
              style={{ maxHeight: 'calc(100vh - 180px)' }}
              data-testid="driver-summary-drawer"
            >
              <div
                className="px-4 py-3 flex items-center gap-3"
                style={{ backgroundColor: driverDetail?.driver?.color || '#475569', color: '#fff' }}
              >
                <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center font-bold">
                  {initials(driverDetail?.driver?.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{driverDetail?.driver?.name || 'Loading…'}</p>
                  <p className="text-xs opacity-90 truncate">
                    {driverDetail?.driver?.vehicle || ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeDriver}
                  className="text-white/80 hover:text-white"
                  data-testid="close-driver-drawer-btn"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                {loadingDetail ? (
                  <p className="text-sm text-gray-500">Loading…</p>
                ) : (
                  <>
                    {/* Quick contact */}
                    {driverDetail?.driver && (
                      <div className="flex gap-2 mb-4">
                        {driverDetail.driver.phone && (
                          <a
                            href={`tel:${driverDetail.driver.phone}`}
                            className="flex-1 bg-gray-900 text-white text-sm font-medium rounded-lg py-2 text-center flex items-center justify-center gap-1.5 hover:bg-black"
                          >
                            <Phone className="h-3.5 w-3.5" /> Call
                          </a>
                        )}
                        <Button
                          variant="outline"
                          className="flex-1 border-gray-300"
                          onClick={() => window.open(`/admin/drivers/${encodeURIComponent(driverDetail.driver.id)}`, '_blank')}
                        >
                          Full Profile
                        </Button>
                      </div>
                    )}

                    {/* Current situation */}
                    {selectedLivePin ? (
                      <div className="mb-4">
                        <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Current Job</p>
                        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                              <Radio className="h-3 w-3" /> LIVE
                            </span>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white text-emerald-700 border border-emerald-200">
                              {STATUS_META[selectedLivePin.driverStatus]?.label || selectedLivePin.driverStatus}
                            </span>
                          </div>
                          <p className="font-semibold text-black text-sm">
                            {selectedLivePin.firstName} {selectedLivePin.lastName} · {selectedLivePin.pickupTime}
                          </p>
                          <p className="text-xs text-gray-700 mt-1 flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-gray-400" /> {selectedLivePin.pickupLocation}
                          </p>
                          <p className="text-xs text-gray-600 pl-4">&rarr; {selectedLivePin.dropoffLocation}</p>
                          <p className="text-[11px] text-gray-500 mt-2 flex items-center gap-1">
                            <Clock className="h-3 w-3" /> Last ping {timeAgo(selectedLivePin.driverLocationUpdatedAt)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600 mb-4">No live job. Driver is idle.</p>
                    )}

                    {/* Upcoming */}
                    {driverDetail?.upcoming && driverDetail.upcoming.length > 0 && (
                      <div className="mb-2">
                        <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2 flex items-center justify-between">
                          Upcoming Jobs
                          <span className="text-gray-400 normal-case tracking-normal">{driverDetail.upcoming.length}</span>
                        </p>
                        <div className="divide-y divide-gray-100" data-testid="drawer-upcoming-list">
                          {driverDetail.upcoming.slice(0, 5).map((b) => (
                            <div key={b.id} className="py-2.5">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-black truncate">
                                    {b.firstName} {b.lastName}
                                  </p>
                                  <p className="text-xs text-gray-600 truncate flex items-center gap-1">
                                    <MapPin className="h-3 w-3 text-gray-400" />
                                    {b.pickupLocation}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate pl-4">&rarr; {b.dropoffLocation}</p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-xs font-bold text-black">{b.pickupTime}</p>
                                  <p className="text-[11px] text-gray-500">{formatUkDate(b.pickupDate)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                          {driverDetail.upcoming.length > 5 && (
                            <p className="text-[11px] text-gray-500 pt-2">
                              + {driverDetail.upcoming.length - 5} more — see full profile
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {driverDetail?.upcoming?.length === 0 && (
                      <p className="text-sm text-gray-500">No upcoming jobs scheduled.</p>
                    )}

                    {/* Footer stat row */}
                    {driverDetail?.stats && (
                      <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-[11px] text-gray-500">Live</p>
                          <p className="text-sm font-bold text-emerald-700">{driverDetail.stats.live}</p>
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-500">Upcoming</p>
                          <p className="text-sm font-bold text-black">{driverDetail.stats.upcoming}</p>
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-500">Total</p>
                          <p className="text-sm font-bold text-black">{driverDetail.stats.total}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default LiveTracking;
